import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Loader2,
  Plus,
  Target,
} from 'lucide-react';
import { toast } from 'sonner';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { useData } from '../../context/DataContext';
import AccessRestricted from '../../components/AccessRestricted';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { performanceApi } from '../../utils/performance-api';
import type {
  PerformanceResultRow,
  PerformanceSchedule,
  PerformanceScheduleDetail,
  PerformanceSchedulePayload,
} from '../../types/performance.types';
import {
  formatPerformanceDate,
  monthSpan,
  scheduleStatusTone,
} from '../../utils/performance';
import { PerformancePill } from '../../components/performance/PerformancePrimitives';
import { ScheduleFormDialog } from '../../components/performance/ScheduleFormDialog';
import { CalibrationDialog } from '../../components/performance/CalibrationDialog';
import { OverviewTab } from '../../components/performance/settings/OverviewTab';
import { GoalsTab } from '../../components/performance/settings/GoalsTab';
import { ReviewTab } from '../../components/performance/settings/ReviewTab';
import { ParticipantsTab } from '../../components/performance/settings/ParticipantsTab';
import { ResultsTab } from '../../components/performance/settings/ResultsTab';

type TabValue = 'overview' | 'goals' | 'review' | 'participants' | 'results';

const SettingsSkeleton: React.FC = () => (
  <div className="mx-auto w-full max-w-[1400px] animate-pulse space-y-5">
    <div className="h-10 w-72 rounded-md bg-muted" />
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="h-24 rounded-xl bg-muted" />
      ))}
    </div>
    <div className="h-64 rounded-xl bg-muted" />
  </div>
);

const MetricCard: React.FC<{ label: string; value: string; detail: string }> = ({
  label,
  value,
  detail,
}) => (
  <div className="rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-md">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <p className="mt-1.5 text-3xl font-semibold tracking-tight tabular-nums text-foreground">
      {value}
    </p>
    <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
  </div>
);

export default function PerformanceSettingsPage() {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const ability = useAbility(AbilityContext);
  const { users, departments } = useData();
  const selectableDepartments = departments.filter(
    (department) => !department.deleted && !department.archived
  );

  const canRead = ability.can(ACTIONS.READ, RESOURCES.PERFORMANCE_SCHEDULE);
  const canApprove = ability.can(ACTIONS.APPROVE, RESOURCES.PERFORMANCE_CALIBRATION);

  const [schedules, setSchedules] = React.useState<PerformanceSchedule[]>([]);
  const [detail, setDetail] = React.useState<PerformanceScheduleDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<TabValue>('overview');
  const [scheduleDialog, setScheduleDialog] = React.useState<{
    open: boolean;
    schedule?: PerformanceSchedule | null;
  }>({ open: false });
  const [calibrationResult, setCalibrationResult] = React.useState<PerformanceResultRow | null>(
    null
  );
  const [releaseOpen, setReleaseOpen] = React.useState(false);
  const [releaseConfirm, setReleaseConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const selectedId = params.id;

  const loadSchedules = React.useCallback(async () => {
    const rows = await performanceApi.schedules.list();
    setSchedules(rows);
    return rows;
  }, []);

  const loadDetail = React.useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const response = await performanceApi.schedules.get(id);
      setDetail(response);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!canRead) return;
    setLoading(true);
    loadSchedules()
      .catch((requestError) =>
        setError(
          requestError instanceof Error
            ? requestError.message
            : 'Failed to load Performance Settings.'
        )
      )
      .finally(() => setLoading(false));
  }, [canRead, loadSchedules]);

  React.useEffect(() => {
    if (!canRead || !selectedId) {
      setDetail(null);
      return;
    }
    setTab('overview');
    loadDetail(selectedId).catch((requestError) =>
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Failed to load this schedule.'
      )
    );
  }, [canRead, selectedId, loadDetail]);

  const refreshDetail = async () => {
    if (!selectedId) return;
    await Promise.all([loadDetail(selectedId), loadSchedules()]);
  };

  if (!canRead) return <AccessRestricted />;
  if (loading) return <SettingsSkeleton />;

  if (error && !detail) {
    return (
      <div className="mx-auto w-full max-w-[1400px]">
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <p className="font-medium">Performance Settings could not be loaded.</p>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <Button className="mt-4" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const createSchedule = async (payload: PerformanceSchedulePayload) => {
    const created = await performanceApi.schedules.create(payload);
    await loadSchedules();
    toast.success('Schedule created as Draft');
    navigate(`/performance-settings/${created.schedule.id}`);
  };

  const saveSchedule = async (payload: PerformanceSchedulePayload) => {
    if (!scheduleDialog.schedule) {
      await createSchedule(payload);
      return;
    }
    await performanceApi.schedules.update(scheduleDialog.schedule.id, payload);
    await loadSchedules();
    if (selectedId) await loadDetail(selectedId);
    toast.success('Schedule saved');
  };

  const activate = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await performanceApi.schedules.activate(detail.schedule.id);
      toast.success('Schedule activated — employees can now see their goals');
      await refreshDetail();
    } finally {
      setBusy(false);
    }
  };

  const moveToCalibration = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await performanceApi.schedules.calibration(detail.schedule.id);
      toast.success('Moved to Calibration — reviews are now read-only');
      await refreshDetail();
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await performanceApi.schedules.release(detail.schedule.id, releaseConfirm);
      toast.success('Schedule closed and results released');
      setReleaseOpen(false);
      setReleaseConfirm('');
      await refreshDetail();
    } finally {
      setBusy(false);
    }
  };

  const actionHint = (() => {
    if (!detail) return null;
    const schedule = detail.schedule;
    if (schedule.status === 'Draft' && detail.failingChecks) {
      return {
        tone: 'warning' as const,
        text: `Resolve ${detail.failingChecks} item${detail.failingChecks === 1 ? '' : 's'} to activate`,
      };
    }
    if (schedule.status === 'Active') {
      const canCalibrate =
        !schedule.eoy.enabled || !schedule.eoy.start || schedule.today >= schedule.eoy.start;
      if (!canCalibrate) {
        return {
          tone: 'muted' as const,
          text: 'Available once the end-of-year window opens',
        };
      }
    }
    return null;
  })();

  const renderPrimaryAction = () => {
    if (!detail) return null;
    const schedule = detail.schedule;
    if (schedule.status === 'Draft') {
      return (
        <Button onClick={activate} disabled={busy || detail.failingChecks > 0}>
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-2 h-4 w-4" />
          )}
          Activate
        </Button>
      );
    }
    if (schedule.status === 'Active') {
      const canCalibrate =
        !schedule.eoy.enabled || !schedule.eoy.start || schedule.today >= schedule.eoy.start;
      return (
        <Button variant="outline" onClick={moveToCalibration} disabled={busy || !canCalibrate}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Move to Calibration
        </Button>
      );
    }
    if (schedule.status === 'Calibration') {
      return (
        <Button
          onClick={() => setReleaseOpen(true)}
          disabled={busy || !canApprove}
          title={!canApprove ? 'Needs approve:performance_calibration' : undefined}
        >
          Close & Release
        </Button>
      );
    }
    return <PerformancePill tone="slate">Closed</PerformancePill>;
  };

  /* ------------------------------------------------------------------ list */

  if (!selectedId) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-border bg-card shadow-sm">
              <Target className="h-5 w-5 text-foreground" />
            </span>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Performance Settings
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Schedules, goal cascades, review windows and calibration.
              </p>
            </div>
          </div>
          <Button onClick={() => setScheduleDialog({ open: true, schedule: null })}>
            <Plus className="mr-2 h-4 w-4" /> New Schedule
          </Button>
        </div>

        <div>
          <h2 className="text-sm font-semibold">Schedules</h2>
          <p className="text-sm text-muted-foreground">
            Only one schedule can be Active or in Calibration at a time.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {schedules.map((schedule) => (
              <button
                key={schedule.id}
                type="button"
                onClick={() => navigate(`/performance-settings/${schedule.id}`)}
                className="flex flex-col gap-1.5 rounded-xl border border-border bg-card p-4 text-left transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-foreground">{schedule.name}</span>
                  <PerformancePill tone={scheduleStatusTone(schedule.status)}>
                    {schedule.status}
                  </PerformancePill>
                </div>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatPerformanceDate(schedule.startDate)} –{' '}
                  {formatPerformanceDate(schedule.endDate)} ·{' '}
                  {monthSpan(schedule.startDate, schedule.endDate)} months
                </span>
                <span className="mt-1 inline-flex items-center gap-1 text-xs text-link">
                  Open <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </button>
            ))}
            {!schedules.length ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground sm:col-span-2 xl:col-span-3">
                No performance schedules yet. Create one to open the module.
              </div>
            ) : null}
          </div>
        </div>

        <ScheduleFormDialog
          open={scheduleDialog.open}
          onOpenChange={(open) => setScheduleDialog((current) => ({ ...current, open }))}
          schedule={scheduleDialog.schedule}
          onSubmit={saveSchedule}
        />
      </div>
    );
  }

  /* ---------------------------------------------------------------- detail */

  if (!detail) {
    return (
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading schedule…
      </div>
    );
  }

  const schedule = detail.schedule;
  const pct = (value: number, total: number) =>
    total ? `${Math.round((value / total) * 100)}%` : '–';

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 md:space-y-5">
      <button
        type="button"
        onClick={() => navigate('/performance-settings')}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Performance Settings
      </button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              {schedule.name}
            </h1>
            <PerformancePill tone={scheduleStatusTone(schedule.status)}>
              {schedule.status}
            </PerformancePill>
          </div>
          <p className="mt-1 text-sm tabular-nums text-muted-foreground">
            {formatPerformanceDate(schedule.startDate)} –{' '}
            {formatPerformanceDate(schedule.endDate)} ·{' '}
            {monthSpan(schedule.startDate, schedule.endDate)} months
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          <div className="flex flex-wrap items-center gap-2">
            {schedule.status !== 'Closed' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScheduleDialog({ open: true, schedule })}
              >
                Edit schedule
              </Button>
            ) : null}
            {renderPrimaryAction()}
          </div>
          {actionHint ? (
            <p
              className={
                actionHint.tone === 'warning'
                  ? 'text-xs text-warning sm:text-right'
                  : 'text-xs text-muted-foreground sm:text-right'
              }
            >
              {actionHint.text}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Participants"
          value={String(detail.metrics.participants)}
          detail={`${detail.metrics.excluded} excluded`}
        />
        <MetricCard
          label="Goals Set"
          value={pct(detail.metrics.goalsSet, detail.metrics.participants)}
          detail={`${detail.metrics.goalsSet} of ${detail.metrics.participants} people`}
        />
        <MetricCard
          label="Mid-Year Complete"
          value={
            schedule.mid.enabled
              ? pct(detail.metrics.midComplete, detail.metrics.participants)
              : '–'
          }
          detail={
            schedule.mid.enabled
              ? `${detail.metrics.midComplete} manager-submitted`
              : 'Mid-year review off'
          }
        />
        <MetricCard
          label="End-of-Year Complete"
          value={
            schedule.eoy.enabled
              ? pct(detail.metrics.eoyComplete, detail.metrics.participants)
              : '–'
          }
          detail={
            schedule.eoy.enabled
              ? `${detail.metrics.eoyComplete} manager-submitted`
              : 'End-of-year review off'
          }
        />
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as TabValue)}>
        <TabsList className="w-full justify-start overflow-x-auto p-1">
          {(
            [
              ['overview', 'Overview'],
              ['goals', 'Goals'],
              ['review', 'Review'],
              ['participants', 'Participants'],
              ['results', 'Calibration & Results'],
            ] as [TabValue, string][]
          ).map(([value, label]) => (
            <TabsTrigger key={value} value={value} className="flex-none px-3">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {detailLoading ? (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Refreshing…
        </div>
      ) : null}

      {tab === 'overview' ? (
        <OverviewTab
          detail={detail}
          onEditSchedule={() => setScheduleDialog({ open: true, schedule })}
        />
      ) : null}

      {tab === 'goals' ? (
        <GoalsTab
          detail={detail}
          users={users}
          departments={selectableDepartments}
          onChanged={refreshDetail}
        />
      ) : null}

      {tab === 'review' ? <ReviewTab detail={detail} onChanged={refreshDetail} /> : null}

      {tab === 'participants' ? (
        <ParticipantsTab
          detail={detail}
          departments={selectableDepartments}
          onChanged={refreshDetail}
        />
      ) : null}

      {tab === 'results' ? (
        <ResultsTab detail={detail} onCalibrate={setCalibrationResult} />
      ) : null}

      <ScheduleFormDialog
        open={scheduleDialog.open}
        onOpenChange={(open) => setScheduleDialog((current) => ({ ...current, open }))}
        schedule={scheduleDialog.schedule}
        onSubmit={saveSchedule}
      />

      <CalibrationDialog
        open={Boolean(calibrationResult)}
        onOpenChange={(open) => {
          if (!open) setCalibrationResult(null);
        }}
        schedule={schedule}
        result={calibrationResult}
        levels={detail.levels}
        calibrations={detail.calibrations}
        onSaved={refreshDetail}
      />

      <Dialog open={releaseOpen} onOpenChange={setReleaseOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Close & Release</DialogTitle>
            <DialogDescription>
              This closes the schedule and releases results to employees. It cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-[10px] border border-border bg-muted/30 p-3 text-sm">
              <p className="font-medium">
                {detail.metrics.eoyComplete} of {detail.metrics.participants} end-of-year reviews
                manager-submitted
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Every included participant needs a submitted manager review, or must be excluded
                first.
              </p>
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">
                Type <span className="font-mono text-xs">RELEASE</span> to confirm
              </p>
              <Input
                value={releaseConfirm}
                onChange={(event) => setReleaseConfirm(event.target.value)}
                placeholder="RELEASE"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={release}
              disabled={busy || releaseConfirm.trim().toUpperCase() !== 'RELEASE'}
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Close & Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
